import api from "@/lib/api";
import { logger } from "@/lib/logger";

// Loads the Google Maps JavaScript API once, with the browser key from
// GET /api/config/defaults. Resolves to `window.google`, or null when maps
// aren't configured (callers fall back to plain inputs).
//
// Uses the Places API (New) classes (AutocompleteSuggestion, Place) — the
// legacy Autocomplete widget isn't available to new Google Cloud projects.
// The browser key must allow: Maps JavaScript API + Places API (New).

let _promise = null;
const CALLBACK = "__pawbazaarMapsReady";

export function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google);
  if (_promise) return _promise;
  _promise = api.get("/config/defaults").then(({ data }) => {
    const key = data.google_maps_browser_key || data.google_maps_api_key;
    if (!key) return null;
    return new Promise((resolve, reject) => {
      window[CALLBACK] = () => resolve(window.google);
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&loading=async&region=IN&language=en&callback=${CALLBACK}`;
      s.async = true;
      s.onerror = () => reject(new Error("Failed to load Google Maps"));
      document.head.appendChild(s);
    });
  }).catch((e) => {
    logger.warn("Google Maps failed to load:", e);
    _promise = null;   // allow a retry later
    return null;
  });
  return _promise;
}

// Default map centre when we know nothing about the customer (Mumbai).
export const DEFAULT_CENTER = { lat: 19.076, lng: 72.8777 };

/** Turn Places (New) / Geocoding address components into our address fields. */
export function parseAddressComponents(components = []) {
  const by = {};
  for (const c of components) {
    const name = c.longText ?? c.long_name;
    for (const t of c.types || []) if (!(t in by)) by[t] = name;
  }
  return {
    pincode: by.postal_code || "",
    city: by.locality || by.administrative_area_level_3 || by.administrative_area_level_2 || "",
    state: by.administrative_area_level_1 || "",
    area: by.sublocality_level_1 || by.sublocality || by.neighborhood || by.locality || "",
  };
}

/** Browser geolocation as a promise of {lat, lng}. */
export function currentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Your browser can't share its location."));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(err.code === 1
        ? "Location permission was denied. Allow it in your browser, or search for your address."
        : "Couldn't read your location. Search for your address instead.")),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  });
}

export const directionsUrl = (p) => (p && p.lat != null && p.lng != null)
  ? `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}` : null;

function mergeComponents(lists) {
  const out = { pincode: "", city: "", state: "", area: "" };
  for (const comps of lists) {
    const p = parseAddressComponents(comps);
    for (const k of Object.keys(out)) if (!out[k] && p[k]) out[k] = p[k];
  }
  return out;
}

/**
 * Name a map point for the "use my location" chip: {area, city, pincode,
 * formatted_address} or null. Tries our backend (server key, Geocoding API),
 * then the browser Geocoder, then nearby places (Places API (New), which the
 * browser key already has for search) — so a key missing one API still names
 * the spot.
 */
export async function nameForPoint(p) {
  try {
    const { data } = await api.get("/geocode/reverse", { params: p });
    if (data.ok && (data.area || data.city || data.pincode)) return data;
  } catch { /* try the browser */ }
  const google = await loadGoogleMaps();
  if (!google) return null;
  try {
    const { Geocoder } = await google.maps.importLibrary("geocoding");
    const { results } = await new Geocoder().geocode({ location: p, region: "IN" });
    if (results?.length) {
      const m = mergeComponents(results.slice(0, 6).map(r => r.address_components));
      if (m.area || m.city || m.pincode) return { ...m, formatted_address: results[0].formatted_address };
    }
  } catch (e) { logger.warn("Browser geocoder unavailable:", e?.message || e); }
  try {
    const { Place } = await google.maps.importLibrary("places");
    const { places } = await Place.searchNearby({
      fields: ["addressComponents", "formattedAddress"],
      locationRestriction: { center: p, radius: 300 },
      maxResultCount: 5,
    });
    if (places?.length) {
      const m = mergeComponents(places.map(pl => pl.addressComponents || []));
      if (m.area || m.city || m.pincode) return { ...m, formatted_address: null };
    }
  } catch (e) { logger.warn("Nearby place lookup failed:", e?.message || e); }
  return null;
}
